/* KFH chrome: announcement rotation, sticky header state, mega menu, mobile menu,
   cart count, footer columns. Loaded by kfh-announcement, kfh-header and kfh-footer;
   guarded so it runs once. */
(() => {
  if (window.KFHChrome) return;
  window.KFHChrome = true;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Announcement bar (mobile rotation) ---------- */
  const initAnnounce = (bar) => {
    if (bar.dataset.ready) return;
    bar.dataset.ready = '1';
    const items = () => [...bar.querySelectorAll('.kfh-announce__item')].filter((i) => !i.hidden);
    let index = 0;
    let timer;
    const show = (n) => {
      const list = items();
      bar.classList.toggle('is-single', list.length < 2);
      if (!list.length) return;
      index = (n + list.length) % list.length;
      list.forEach((item, i) => item.classList.toggle('is-active', i === index));
    };
    const start = () => {
      clearInterval(timer);
      if (reduceMotion.matches || desktop.matches) return;
      timer = setInterval(() => show(index + 1), Number(bar.dataset.interval) || 4000);
    };
    bar.querySelector('[data-kfh-announce-prev]').addEventListener('click', () => { show(index - 1); start(); });
    bar.querySelector('[data-kfh-announce-next]').addEventListener('click', () => { show(index + 1); start(); });
    bar.addEventListener('mouseenter', () => clearInterval(timer));
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', () => clearInterval(timer));
    desktop.addEventListener('change', start);
    // Sale items unhide after kfh-base.js runs; recount once it has.
    requestAnimationFrame(() => { show(0); start(); });
  };

  /* ---------- Header ---------- */
  const initHeader = (header) => {
    if (header.dataset.ready) return;
    header.dataset.ready = '1';

    // Slim state after 40px of scroll.
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Mega menu: hover or click on the trigger, closes on leaving the header, Escape, or scrim click.
    const trigger = header.querySelector('[data-kfh-mega-trigger]');
    const mega = header.querySelector('[data-kfh-mega]');
    const scrim = header.parentElement.querySelector('[data-kfh-mega-scrim]');
    if (trigger && mega) {
      let closeTimer;
      const open = () => {
        clearTimeout(closeTimer); mega.hidden = false; trigger.setAttribute('aria-expanded', 'true');
        if (scrim) scrim.hidden = false;
      };
      const close = () => {
        mega.hidden = true; trigger.setAttribute('aria-expanded', 'false');
        if (scrim) scrim.hidden = true;
      };
      trigger.addEventListener('click', () => (mega.hidden ? open() : close()));
      trigger.addEventListener('mouseenter', () => { if (desktop.matches) open(); });
      header.querySelectorAll('.kfh-nav__link:not([data-kfh-mega-trigger])').forEach((a) => a.addEventListener('mouseenter', close));
      header.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 120); });
      header.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      if (scrim) scrim.addEventListener('click', close);
      header.addEventListener('focusout', (e) => { if (!header.contains(e.relatedTarget)) close(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !mega.hidden) { close(); trigger.focus(); }
      });
    }

    // Mobile menu.
    const menu = document.getElementById('KfhMobileMenu');
    const opener = header.querySelector('[data-kfh-menu-open]');
    if (menu && opener) {
      const open = () => {
        menu.hidden = false;
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.classList.add('kfh-menu-open');
        const first = menu.querySelector('[data-kfh-menu-close]');
        if (first) first.focus();
      };
      const close = () => {
        menu.hidden = true;
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
        document.documentElement.classList.remove('kfh-menu-open');
        opener.focus();
      };
      opener.addEventListener('click', open);
      menu.querySelector('[data-kfh-menu-close]').addEventListener('click', close);
      menu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
        if (e.key !== 'Tab') return;
        // Keep focus inside the menu.
        const focusables = [...menu.querySelectorAll('a[href], button:not([disabled])')].filter((n) => n.offsetParent !== null);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      desktop.addEventListener('change', () => { if (desktop.matches && !menu.hidden) close(); });
    }
  };

  /* ---------- Cart count (header + mobile menu) ---------- */
  const setCount = (count) => {
    document.querySelectorAll('[data-kfh-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count < 1;
    });
  };
  const refreshCount = async () => {
    try {
      const cart = await (await fetch(`${window.KFH_CONFIG ? KFH_CONFIG.routes.cart : '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
      setCount(cart.item_count);
    } catch (e) { /* count stays as rendered */ }
  };
  document.addEventListener('kfh:cart:added', refreshCount);
  document.addEventListener('kfh:cart:changed', (e) => {
    if (e.detail && typeof e.detail.item_count === 'number') setCount(e.detail.item_count);
    else refreshCount();
  });

  /* ---------- Footer columns: accordion on mobile, always open on desktop ---------- */
  const syncFooter = () => {
    document.querySelectorAll('[data-kfh-foot-col]').forEach((col) => {
      if (desktop.matches) col.open = true;
      else if (!col.dataset.touched) col.open = false; // phones: every column starts closed
    });
  };
  document.addEventListener('toggle', (e) => {
    if (e.target.matches && e.target.matches('[data-kfh-foot-col]') && !desktop.matches) e.target.dataset.touched = '1';
  }, true);
  desktop.addEventListener('change', syncFooter);

  const init = (root = document) => {
    root.querySelectorAll('[data-kfh-announce]').forEach(initAnnounce);
    root.querySelectorAll('[data-kfh-header]').forEach(initHeader);
    syncFooter();
  };
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
